import { ToggleLabelPosition } from '@capsicum/common';
import { type ChangeEvent, type DetailedHTMLProps, forwardRef, type InputHTMLAttributes } from 'react';
import { type FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { useRTL } from '../../../../hooks/helpers/useRTL';
import { InteractionLayer } from '../../../atoms/interaction-layer/InteractionLayer';

export type BaseToggleInputProps = Omit<
	DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	'onChange' | 'ref' | 'value'
> & {
	error?: FieldError;
	label?: string;
	labelPosition?: ToggleLabelPosition;
	onChange: (checked: boolean) => void;
	testID: string;
	value?: boolean;
};
/**
 * @param {BaseToggleInputProps} props - The component's props
 * @param {string} label - label to display
 * @param {string} defaultChecked - initial checked state
 * @param {string} disabled - if the toggle is disabled
 * @param {ToggleLabelPosition} labelPosition - position of the label relative to the toggle (default: BEFORE)
 * @param {string} testID - test ID for the component
 * @param {function} onChange - on component click change callback
 */
export const BaseToggleInput = forwardRef<HTMLInputElement, BaseToggleInputProps>(
	(
		{ disabled = false, label = '', labelPosition = ToggleLabelPosition.BEFORE, onChange, testID, value, ...props },
		ref
	) => {
		const { isRTL } = useRTL();

		const labelElement = label ? <S.Label htmlFor={`capsicum-toggle-${testID}`}>{label}</S.Label> : null;
		const toggleElement = (
			<S.ILayer disabled={disabled} pressable={false} onClick={() => onChange?.(!value)}>
				<>
					<S.ToggleStyled
						checked={!!value}
						id={`capsicum-toggle-${testID}`}
						ref={ref}
						type="checkbox"
						onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
						{...props}
					/>
					<S.ToggleThumb $disabled={disabled} $isRTL={isRTL} />
				</>
			</S.ILayer>
		);

		return (
			<S.Container data-testid={testID}>
				{labelPosition === ToggleLabelPosition.AFTER ? (
					<>
						{toggleElement}
						{labelElement}
					</>
				) : (
					<>
						{labelElement}
						{toggleElement}
					</>
				)}
			</S.Container>
		);
	}
);

BaseToggleInput.displayName = 'BaseToggle';

namespace S {
	export const Container = styled.div`
		display: flex;
		align-items: center;
		gap: 15px;
		cursor: pointer;
		${({ theme }) => theme.fonts.text.text3};
	`;
	export const Label = styled.label`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const ILayer = styled(InteractionLayer)`
		position: relative;
	`;
	export const ToggleThumb = styled.div<{ $disabled: boolean; $isRTL: boolean }>`
		opacity: ${({ $disabled }) => ($disabled ? '0.5' : '1')};
		position: relative;
		transform: rotate(${({ $isRTL }) => ($isRTL ? '180' : '0')}deg);
		box-sizing: border-box;
		width: 48px;
		height: 28px;
		background: ${({ theme }) => theme?.colors.icons.main.unselected};
		border-radius: 32px;
		border: 1px solid #e4e4e4;
		padding: 4px;
		transition: 300ms all;

		&:before {
			transition: 300ms all;
			content: '';
			position: absolute;
			width: 20px;
			height: 20px;
			border-radius: 35px;
			box-shadow: ${({ $isRTL }) =>
				$isRTL ? '2px 1px 5px rgba(0, 0, 0, 0.3)' : '0px 2px 9px rgba(0, 0, 0, 0.3)'};
			top: 50%;
			left: 4px;
			background: ${({ theme }) => theme?.colors.backgrounds.main.container};
			transform: translate(0, -50%);
		}
	`;
	export const ToggleStyled = styled.input`
		opacity: 0;
		position: absolute;

		&:checked + ${ToggleThumb} {
			background: ${({ theme }) => theme?.colors.icons.brand.enabled};

			&:before {
				transform: translate(18px, -50%);
			}
		}
	`;
}
